import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Chip, Link, Skeleton, Svg, Text } from '@hopskipdrive/react-components';
import EmailIcon from '@mui/icons-material/Email';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';

import { Card } from '../../../../shared/components/Card';
import { CopyToClipboard } from '../../../../shared/components/CopyToClipboard';
import { ProfileImage } from '../../../../shared/components/ProfileImage';
import { ProfileInitials } from '../../../../shared/components/ProfileInitials';
import { colorPalette, colorTokens } from '../../../../shared/components/variables';

import { ContactSection, Header, InfoSection } from './OrganizerDetails.styles';

interface OrganizerDetailsProps {
  organizerSlug: string;
}
import { ContactCard } from '../../../../shared/components/ContactCard';
import { OrganizerIcon } from '../../../../shared/components/Icons';
import { useOrganizerDetailsQuery } from '../../../hooks/useOrganizerDetailsQuery';

export const OrganizerDetails: FC<OrganizerDetailsProps> = ({ organizerSlug }) => {
  const { rideSlug } = useParams();
  const { data: organizerDetailsResult, isFetching } = useOrganizerDetailsQuery({
    rideSlug: rideSlug || '',
    organizerSlug: organizerSlug || '',
  });

  return (
    <Card>
      <Header>
        <Box display="flex" alignItems="center" gap="12px">
          <OrganizerIcon style={{ display: 'block', height: '24px', width: '24px', alignSelf: 'center' }} />
          <Text variant="h6" textColor="primaryText">
            Organizer
          </Text>
          <CopyToClipboard
            toastMessage="Slug Copied"
            text={organizerDetailsResult?.data?.slug}
            variant="h6"
            color={colorTokens.text.secondary}
          />
          <Chip
            type="square"
            label={organizerDetailsResult?.data?.leadOrganizer ? 'Lead Organizer' : 'Participating  Organizer'}
            style={{
              border: 0,
              backgroundColor: colorPalette.neutral650,
              padding: '15px 16px',
            }}
            {...(organizerDetailsResult?.data?.leadOrganizer && { icon: <Svg icon="star" /> })}
          />
        </Box>
      </Header>
      {organizerDetailsResult?.data?.phoneErrors.map((phoneError) => (
        <Alert
          key={phoneError}
          severity="warning"
          message={`Error: ${phoneError}`}
          variant="outlined"
          action={
            <Link
              data-testid="phone-error-link"
              target="_blank"
              href={organizerDetailsResult?.data?.profileUrl}
              style={{
                textDecoration: 'none',
              }}
            >
              <Text
                variant="button"
                style={{
                  color: colorTokens.warning.dark,
                }}
              >
                See in Admin
              </Text>
            </Link>
          }
          style={{ fontSize: '20px', width: '100%' }}
        />
      ))}
      <Box display="flex" gap="20px" sx={{ width: '100%' }}>
        <Box sx={{ width: '50%' }} alignItems="flex-end">
          <Box mb={3}>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Link
                target="_blank"
                variant="h5"
                underline="hover"
                href={organizerDetailsResult?.data?.profileUrl}
                style={{ color: colorTokens.text.primary, fontWeight: 500 }}
              >
                {`${organizerDetailsResult?.data?.firstName} ${organizerDetailsResult?.data?.lastName}`}
              </Link>
            )}
          </Box>
          <Box mb={3}>
            <OrganizerInfoContainer isFetching={isFetching}>
              <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
              <CopyToClipboard
                toastMessage="Phone Number Copied"
                text={organizerDetailsResult?.data?.mobilePhoneNumber}
                variant="h6"
                color={colorTokens.text.secondary}
              />
            </OrganizerInfoContainer>
            <OrganizerInfoContainer isFetching={isFetching}>
              <EmailIcon style={{ color: colorTokens.text.secondary }} />
              <CopyToClipboard
                toastMessage="Email Copied"
                text={organizerDetailsResult?.data?.email}
                variant="h6"
                color={colorTokens.text.secondary}
                truncate={true}
              />
            </OrganizerInfoContainer>
            <OrganizerInfoContainer isFetching={isFetching}>
              <Svg icon="sidebar-pin" width="24px" height="24px" style={{ color: colorTokens.text.secondary }} />
              <CopyToClipboard
                toastMessage="Address Copied"
                text={organizerDetailsResult?.data?.address}
                variant="h6"
                color={colorTokens.text.secondary}
              />
            </OrganizerInfoContainer>
            <Box mb={3}>
              <Box display="flex" flexDirection="row" alignItems="center" gap="9px">
                <Svg icon="directions-car-filled" width="16" height="16" />
                <Text variant="caption" textColor="secondaryText" fontWeight={500}>
                  {organizerDetailsResult?.data?.rideCount} Trips Activated {organizerDetailsResult?.data?.activatedOn}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: '50%' }} alignItems="flex-end">
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText" mb={1}>
              No-Show Wait Time
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Text variant="h6">{organizerDetailsResult?.data?.waitTimeBeforeLeaving}</Text>
            )}
          </InfoSection>
          <InfoSection>
            <Text variant="subtitle1" textColor="secondaryText" mb={1}>
              Passengers on this ride
            </Text>
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <>
                {organizerDetailsResult?.data?.passengers.map((rider) => (
                  <Box display="flex" justifyContent="flex-start" alignItems="center" gap="12px" key={rider.slug}>
                    {rider.profilePictureUrl ? (
                      <ProfileImage imgSrc={rider.profilePictureUrl} key={rider.slug} />
                    ) : (
                      <ProfileInitials
                        key={rider.slug}
                        fullName={`${rider.firstName} ${rider.lastName}`}
                        size={33}
                        bgColor="#7FCFDE"
                      />
                    )}
                    <Text variant="h6">{`${rider.firstName} ${rider.lastName}`}</Text>
                  </Box>
                ))}
              </>
            )}
          </InfoSection>
        </Box>
      </Box>
      <ContactSection>
        <ContactCard>
          <ContactCard.Title>
            <Svg icon="school-cc" width="24px" height="24px" style={{ color: colorTokens.primary.main }} />
            {isFetching ? (
              <Skeleton variant="text" width={263} height={42} />
            ) : (
              <Link
                target="_blank"
                variant="h6"
                underline="hover"
                href={organizerDetailsResult?.data?.accountDetails?.accountUrl}
                style={{ color: colorTokens.primary.main, fontWeight: 500 }}
              >
                {`Account: ${organizerDetailsResult?.data?.accountDetails?.accountName}`}
              </Link>
            )}
            <CopyToClipboard
              toastMessage="Slug Copied"
              text={organizerDetailsResult?.data?.accountDetails?.accountSlug}
              variant="subtitle1"
              color={colorTokens.text.secondary}
            />
          </ContactCard.Title>
        </ContactCard>
      </ContactSection>
    </Card>
  );
};

const OrganizerInfoContainer = ({ isFetching, children }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="9px" mb={3}>
      {isFetching ? (
        <>
          <Skeleton data-testid="organizer-info-skeleton-loader" variant="text" width={10} height={42} />
          <Skeleton variant="text" width={263} height={42} />
          <Skeleton variant="text" width={10} height={42} />
        </>
      ) : (
        children
      )}
    </Box>
  );
};

export const OrganizerInfoSkeleton = () => (
  <>
    <Box display="flex" flexDirection="row" alignItems="center" gap="9px" mb={3}>
      <Skeleton data-testid="organizer-info-skeleton-loader" variant="text" width={10} height={42} />
      <Skeleton variant="text" width={263} height={42} />
      <Skeleton variant="text" width={10} height={42} />
    </Box>
    <Box display="flex" flexDirection="row" alignItems="center" gap="9px" mb={3}>
      <Skeleton data-testid="organizer-info-skeleton-loader" variant="text" width={10} height={42} />
      <Skeleton variant="text" width={263} height={42} />
      <Skeleton variant="text" width={10} height={42} />
    </Box>
    <Box display="flex" flexDirection="row" alignItems="center" gap="9px" mb={3}>
      <Skeleton data-testid="organizer-info-skeleton-loader" variant="text" width={10} height={42} />
      <Skeleton variant="text" width={263} height={42} />
      <Skeleton variant="text" width={10} height={42} />
    </Box>
  </>
);
